import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Fab,
  Box,
  Chip,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Photo as PhotoIcon,
  ArrowBack as ArrowBackIcon,
  Pets as PetsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { entriesAPI } from '../services/api';
import { TravelEntry } from '../types';
import Loading from '../components/Loading';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { backgroundStyles, componentStyles } from '../theme/fojournTheme';
import { useMemoryTypes } from '../hooks/useMemoryTypes';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const selectedDate = searchParams.get('date');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const limit = 12;

  // Get memory types for display
  const { memoryTypes } = useMemoryTypes();

  // Build query parameters based on whether we have a selected date
  const queryParams = selectedDate 
    ? { date: selectedDate, sortBy: 'entry_date', sortOrder: 'DESC' }
    : { page, limit, sortBy: 'entry_date', sortOrder: 'DESC' };

  const {
    data: entriesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['entries', selectedDate || page, limit],
    queryFn: () => entriesAPI.getEntries(queryParams),
  });

  const handleCreateEntry = () => {
    navigate('/map'); // Navigate to map to create new entry
  };

  const handleViewEntry = (entryId: number) => {
    navigate(`/entry/${entryId}`, {
      state: {
        from: 'dashboard',
        selectedDate: selectedDate
      }
    });
  };

  if (isLoading) return <Loading />;
  if (error) return <Typography color="error">Error loading entries</Typography>;

  const entries = entriesData?.data.entries || [];
  const pagination = entriesData?.data.pagination;

  // Helper function to get the default image for an entry
  const getDefaultImage = (media: any[]) => {
    if (!media || media.length === 0) return null;
    // Find the image marked as default, or fall back to the first image
    const defaultImage = media.find(m => m.fileType === 'image' && m.isDefault);
    const firstImage = media.find(m => m.fileType === 'image');
    return defaultImage || firstImage || media[0];
  };

  // Format the selected date for display
  const formattedDate = selectedDate && isValid(parseISO(selectedDate))
    ? format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')
    : null;

  return (
    <Box sx={backgroundStyles.secondary}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4, minHeight: '100vh' }}>
        {/* Header with optional date and back button */}
        <Fade in timeout={800}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box display="flex" alignItems="center" gap={2}>
              {selectedDate && (
                <IconButton 
                  onClick={() => navigate('/calendar')} 
                  sx={{
                    background: 'rgba(255,255,255,0.9)',
                    '&:hover': {
                      background: 'rgba(255,255,255,1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {selectedDate ? 'Memories for' : 'Your Fojourn'}
                </Typography>
                {formattedDate && (
                  <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                    {formattedDate}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
                sx={{
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: 2,
                  '& .MuiToggleButton-root': {
                    px: { xs: 1, sm: 2 }, // Smaller padding on mobile
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Smaller font on mobile
                  },
                }}
              >
                <ToggleButton value="grid">Grid</ToggleButton>
                <ToggleButton value="list">List</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateEntry}
                size="large"
                sx={{
                  px: { xs: 1.5, sm: 3 }, 
                  py: { xs: 1, sm: 1.5 }, 
                  fontSize: { xs: '0.875rem', sm: '1rem' }, 
                  minWidth: { xs: 'auto', sm: 'auto' },
                  display: { xs: 'none', sm: 'flex' }, // Hide on mobile
                }}
              >
                Add Memory
              </Button>
            </Box>
          </Box>
        </Fade>

        {entries.length === 0 ? (
          <Fade in timeout={1000}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="50vh"
              textAlign="center"
              sx={componentStyles.glassCard}
            >
              <Typography variant="h5" color="primary.main" gutterBottom sx={{ fontWeight: 600 }}>
                {selectedDate ? 'No travel memories for this date' : 'No travel memories yet'}
              </Typography>
              <Typography variant="body1" color="textSecondary" mb={4} sx={{ maxWidth: 500 }}>
                {selectedDate 
                  ? 'Start documenting your travels for this day by adding a memory'
                  : 'Start documenting your travels by adding your first memory. Capture the moments that matter!'
                }
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateEntry}
                size="large"
                sx={{
                  px: { xs: 2, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                }}
              >
                Create Your First Memory
              </Button>
            </Box>
          </Fade>
        ) : (
          <>
            {viewMode === 'grid' ? (
              // Grid View
              <Fade in timeout={1200}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 3 }}>
                  {entries.map((entry: TravelEntry, index) => (
                    <Fade in timeout={800 + index * 100} key={entry.id}>
                      <Card 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                          },
                        }}
                        onClick={() => handleViewEntry(entry.id)}
                      >
                        {entry.media && entry.media.length > 0 && (
                          <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                            <AuthenticatedImage
                              src={getDefaultImage(entry.media)?.thumbnailUrl || getDefaultImage(entry.media)?.url}
                              alt={entry.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              loading="lazy"
                            />
                            {entry.media.length > 1 && (
                              <Chip
                                icon={<PhotoIcon />}
                                label={`+${entry.media.length - 1}`}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                }}
                              />
                            )}
                          </Box>
                        )}
                        
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h6" component="h2">
                            {entry.title}
                          </Typography>
                          
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="textSecondary">
                              {entry.entryDate && !isNaN(new Date(entry.entryDate).getTime()) 
                                ? format(new Date(entry.entryDate), 'MMM dd, yyyy')
                                : 'No date'
                              }
                            </Typography>
                          </Box>

                          {entry.memoryType && (
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              {(() => {
                                const memType = memoryTypes.find(mt => mt.name === entry.memoryType);
                                return (
                                  <Chip
                                    icon={memType?.icon ? (
                                      <Typography component="span" sx={{ fontSize: '0.9em' }}>
                                        {memType.icon}
                                      </Typography>
                                    ) : undefined}
                                    label={memType?.display_name || (entry.memoryType.charAt(0).toUpperCase() + entry.memoryType.slice(1))}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      ...(memType?.color && {
                                        borderColor: memType.color,
                                        color: memType.color,
                                      }),
                                    }}
                                  />
                                );
                              })()}
                            </Box>
                          )}
                          
                          {entry.locationName && (
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="textSecondary">
                                {entry.locationName}
                              </Typography>
                            </Box>
                          )}
                          
                          {entry.isDogFriendly && (
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <PetsIcon fontSize="small" color="success" />
                              <Typography variant="body2" color="success.main">
                                Dog Friendly
                              </Typography>
                            </Box>
                          )}
                          
                          {entry.description && (
                            <Typography variant="body2" color="textSecondary" mb={2}>
                              {entry.description.length > 100
                                ? `${entry.description.substring(0, 100)}...`
                                : entry.description}
                            </Typography>
                          )}
                          
                          {entry.tags && entry.tags.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {entry.tags.slice(0, 3).map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                              ))}
                              {entry.tags.length > 3 && (
                                <Chip label={`+${entry.tags.length - 3}`} size="small" variant="outlined" />
                              )}
                            </Box>
                          )}
                        </CardContent>
                        
                        <CardActions>
                          <Button size="small" onClick={() => handleViewEntry(entry.id)}>
                            View Details
                          </Button>
                        </CardActions>
                      </Card>
                    </Fade>
                  ))}
                </Box>
              </Fade>
            ) : (
              // List View
              <Fade in timeout={1200}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {entries.map((entry: TravelEntry, index) => (
                    <Fade in timeout={800 + index * 100} key={entry.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                        onClick={() => handleViewEntry(entry.id)}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: isMobile ? 1 : 2,
                            minHeight: isMobile ? '56px' : '72px',
                            py: isMobile ? 0.5 : 1
                          }}>
                            {/* Thumbnail Icon - only show on desktop */}
                            {entry.media && entry.media.length > 0 && (
                              <Box sx={{ 
                                display: { xs: 'none', sm: 'block' },
                                position: 'relative',
                                flexShrink: 0
                              }}>
                                <AuthenticatedImage
                                  src={getDefaultImage(entry.media)?.thumbnailUrl || getDefaultImage(entry.media)?.url}
                                  alt={entry.title}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '6px',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                  loading="lazy"
                                />
                                {entry.media.length > 1 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -4,
                                      right: -4,
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '16px',
                                      height: '16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.6rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {entry.media.length}
                                  </Box>
                                )}
                              </Box>
                            )}
                            
                            {/* Title */}
                            <Box sx={{ 
                              flex: isMobile ? '1 1 auto' : '0 1 200px', 
                              minWidth: 0, 
                              display: 'flex', 
                              alignItems: 'center',
                              mr: isMobile ? 1 : 2
                            }}>
                              <Typography 
                                variant={isMobile ? "body1" : "subtitle1"} 
                                component="h3" 
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: isMobile ? '0.9rem' : undefined,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: isMobile ? 1 : 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.2
                                }}
                              >
                                {entry.title}
                              </Typography>
                            </Box>

                            {/* Memory Type */}
                            {entry.memoryType && (
                              <Box sx={{ 
                                flex: '0 0 auto', 
                                display: 'flex', 
                                alignItems: 'center',
                                mr: isMobile ? 1 : 2
                              }}>
                                {(() => {
                                  const memType = memoryTypes.find(mt => mt.name === entry.memoryType);
                                  
                                  if (isMobile) {
                                    // Mobile: Show only icon
                                    return memType?.icon ? (
                                      <Typography 
                                        component="span" 
                                        sx={{ 
                                          fontSize: '1.1em',
                                          color: memType?.color || 'text.secondary',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: '24px',
                                          height: '24px'
                                        }}
                                        title={memType?.display_name || entry.memoryType}
                                      >
                                        {memType.icon}
                                      </Typography>
                                    ) : (
                                      <Box
                                        sx={{
                                          width: '8px',
                                          height: '8px',
                                          borderRadius: '50%',
                                          backgroundColor: memType?.color || 'text.secondary'
                                        }}
                                        title={memType?.display_name || entry.memoryType}
                                      />
                                    );
                                  }
                                  
                                  // Desktop: Show full chip
                                  return (
                                    <Chip
                                      icon={memType?.icon ? (
                                        <Typography component="span" sx={{ fontSize: '0.8em' }}>
                                          {memType.icon}
                                        </Typography>
                                      ) : undefined}
                                      label={memType?.display_name || (entry.memoryType.charAt(0).toUpperCase() + entry.memoryType.slice(1))}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        ...(memType?.color && {
                                          borderColor: memType.color,
                                          color: memType.color,
                                        }),
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            )}

                            {/* Description */}
                            {entry.description && (
                              <Box sx={{ 
                                flex: '1 1 300px', 
                                minWidth: 0,
                                display: { xs: 'none', sm: 'flex' }, 
                                alignItems: 'center',
                                mr: 2
                              }}>
                                <Typography 
                                  variant="body2" 
                                  color="textSecondary"
                                  sx={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.2
                                  }}
                                >
                                  {entry.description}
                                </Typography>
                              </Box>
                            )}
                            
                            {/* Date */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: isMobile ? 0.3 : 0.5,
                              flexShrink: 0
                            }}>
                              <CalendarIcon 
                                fontSize={isMobile ? "small" : "small"} 
                                color="action" 
                                sx={{ fontSize: isMobile ? '16px' : undefined }}
                              />
                              {/* Full date for desktop */}
                              <Typography 
                                variant="body2" 
                                color="textSecondary"
                                sx={{ display: { xs: 'none', sm: 'block' } }}
                              >
                                {entry.entryDate && !isNaN(new Date(entry.entryDate).getTime()) 
                                  ? format(new Date(entry.entryDate), 'MMM dd, yyyy')
                                  : 'No date'
                                }
                              </Typography>
                              {/* Month/Day for mobile */}
                              <Typography 
                                variant="body2" 
                                color="textSecondary"
                                sx={{ 
                                  display: { xs: 'block', sm: 'none' },
                                  fontSize: '0.75rem',
                                  minWidth: '35px'
                                }}
                              >
                                {entry.entryDate && !isNaN(new Date(entry.entryDate).getTime()) 
                                  ? format(new Date(entry.entryDate), 'M/d')
                                  : 'N/A'
                                }
                              </Typography>
                            </Box>
                            
                            {/* View Button */}
                            <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                              <IconButton 
                                size={isMobile ? "small" : "medium"} 
                                color="primary"
                                sx={{
                                  p: isMobile ? 0.5 : 1,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewEntry(entry.id);
                                }}
                                title="View Details"
                              >
                                <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Fade>
                  ))}
                </Box>
              </Fade>
            )}

        {pagination && pagination.totalPages > 1 && (
          <Fade in timeout={1400}>
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="outlined"
                disabled={!pagination.hasPrev}
                onClick={() => setPage(page - 1)}
                sx={{ mr: 1 }}
              >
                Previous
              </Button>
              <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                Page {pagination.page} of {pagination.totalPages}
              </Typography>
              <Button
                variant="outlined"
                disabled={!pagination.hasNext}
                onClick={() => setPage(page + 1)}
                sx={{ ml: 1 }}
              >
                Next
              </Button>
            </Box>
          </Fade>
        )}
      </>
    )}

        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' }, // Show only on mobile
          }}
          onClick={handleCreateEntry}
        >
          <AddIcon />
        </Fab>
      </Container>
    </Box>
  );
};

export default Dashboard;
